import { useEffect, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { TaskForm, type TaskFormState } from './TaskForm'
import { sheetFetch } from '@/lib/sheetFetch'
import type { AssigneeOption, TaskDetail } from '@/types/work'

// In-sheet edit content for TaskDetailSheet. Fetches the edit JSON
// payload (task + assignee_options) on mount, renders TaskForm, and
// PATCHes /work/tasks/:id via sheetFetch so the sheet stays mounted.
//
// On success: invokes onSaved() — the parent flips back to detail mode
// and refreshes both the sheet's data and the index list behind it.
// "Annuler" calls onCancel() to return to detail mode without saving.

interface EditPayload {
  task: TaskDetail
  assignee_options: AssigneeOption[]
}

interface EditTaskSheetContentProps {
  taskId: number
  onSaved: () => void
  onCancel: () => void
}

// Mirrors Edit.tsx#isoToLocalInput — UTC ISO -> local datetime-local.
function isoToLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export function EditTaskSheetContent({ taskId, onSaved, onCancel }: EditTaskSheetContentProps) {
  const [data, setData] = useState<EditPayload | null>(null)
  const [state, setState] = useState<TaskFormState | null>(null)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    setLoading(true)
    const controller = new AbortController()
    fetch(`/work/tasks/${taskId}/edit`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<EditPayload>
      })
      .then((payload) => {
        setData(payload)
        setState({
          title: payload.task.title,
          description: payload.task.description ?? '',
          priority: payload.task.priority,
          task_type: payload.task.task_type,
          due_at: isoToLocalInput(payload.task.due_at),
          assignee_user_id: payload.task.assignee_user_id,
          subject: payload.task.subject,
        })
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err)
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [taskId])

  function update<K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) {
    setState((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!state) return
    setProcessing(true)
    setErrors({})
    try {
      // assignee_user_id is intentionally NOT in this payload —
      // UpdateTask::MUTABLE_KEYS excludes it; reassignment goes through
      // ReassignTask. Same constraint as the standalone Edit page.
      const response = await sheetFetch(`/work/tasks/${taskId}`, {
        method: 'PATCH',
        body: {
          task: {
            title: state.title,
            description: state.description,
            priority: state.priority,
            task_type: state.task_type,
            due_at: state.due_at || null,
            subject_type: state.subject?.type ?? null,
            subject_id: state.subject?.id ?? null,
          },
        },
      })
      if (response.ok) {
        onSaved()
      } else if (response.status === 422) {
        const json = (await response.json()) as { errors: Record<string, string> }
        setErrors(json.errors ?? {})
      } else {
        setErrors({ base: 'Une erreur est survenue.' })
      }
    } catch {
      setErrors({ base: 'Impossible de mettre à jour la tâche.' })
    } finally {
      setProcessing(false)
    }
  }

  if (loading || !state || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Modifier la tâche</h2>
        <p className="text-sm text-muted-foreground">{data.task.title}</p>
      </div>

      {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}

      <TaskForm
        state={state}
        onChange={update}
        onSubmit={submit}
        errors={errors}
        processing={processing}
        assigneeOptions={data.assignee_options}
        assigneeReadOnly
        actions={
          <>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={processing}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              Enregistrer
            </Button>
          </>
        }
      />
    </div>
  )
}
